import React, { useState, useEffect, useRef } from 'react';
import { Database, Check, Target, LayoutList, History, BarChart3, TrendingUp } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';

const EditorialUGCAdsSection: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ leads: 0, qualified: 0, proposal: 0, won: 0 });
  const [showCards, setShowCards] = useState([false, false, false]);
  const [pipelineValue, setPipelineValue] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features = [
    { text: "Visual Sales Pipelines", icon: LayoutList },
    { text: "Integrated Lead Scoring", icon: Target },
    { text: "Smart Lists & Segmentation", icon: Database },
    { text: "Unified Client History", icon: History },
    { text: "Full Attribution Reporting", icon: BarChart3 }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    // Animate counters
    const targetValues = { leads: 24, qualified: 18, proposal: 12, won: 8 };
    const duration = 1500;
    const steps = 30;
    const interval = duration / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      setCounters({
        leads: Math.round(targetValues.leads * progress),
        qualified: Math.round(targetValues.qualified * progress),
        proposal: Math.round(targetValues.proposal * progress),
        won: Math.round(targetValues.won * progress)
      });
      if (step >= steps) clearInterval(timer);
    }, interval);

    // Animate pipeline value
    const valueTimer = setInterval(() => {
      setPipelineValue(prev => {
        if (prev >= 417) return 417;
        return prev + 8;
      });
    }, 30);

    // Show cards staggered
    setTimeout(() => setShowCards([true, false, false]), 600);
    setTimeout(() => setShowCards([true, true, false]), 900);
    setTimeout(() => setShowCards([true, true, true]), 1200);

    return () => {
      clearInterval(timer);
      clearInterval(valueTimer);
    };
  }, [isVisible]);

  return (
    <section ref={sectionRef} className="py-16 px-[7%] sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge with animated border */}
            <AnimatedBorderBadge text="CRM" icon={Database} align="left" />

            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Customer Relationship Magic.
            </h2>

            <p 
              className="text-sm mb-8"
              style={{ color: isDark ? '#AAAAAA' : '#555555' }}
            >
              Turn data chaos into strategic clarity, giving your sales team a clear path to follow with an enterprise-level system that doesn't break the bank.
            </p>

            {/* Feature checklist */}
            <div className="space-y-3 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-start gap-3"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    transition: `all 0.5s ease-out ${index * 100}ms`
                  }}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? 'bg-emerald-900/50' : 'bg-emerald-100'}`}>
                    <Check className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  </div>
                  <span className="text-sm" style={{ color: isDark ? '#CCCCCC' : '#333333' }}>{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => window.location.href = '/pricing#pricing-section'}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] hover:opacity-90"
              style={{
                backgroundColor: isDark ? '#FFFFFF' : '#000000',
                color: isDark ? '#000000' : '#FFFFFF',
              }}
            >
              Get Started with CRM →
            </button>
          </div>

          {/* Right Visual - CRM Dashboard */}
          <div className="relative">
            <div 
              className="rounded-xl p-4 overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8', 
                border: isDark ? '1px solid #333333' : '1px solid #E5E5E5',
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(-4px) scale(1)' : 'translateY(20px) scale(0.95)',
                boxShadow: isVisible ? (isDark ? '0 20px 40px -10px rgba(0, 0, 0, 0.5)' : '0 20px 40px -10px rgba(0, 0, 0, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.1)') : 'none',
                transition: 'all 0.6s ease-out'
              }}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
                  <span className="text-sm font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>Sales Pipeline</span>
                </div>
                <span 
                  className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${isDark ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-600'}`}
                  style={{
                    animation: isVisible ? 'pulse 2s ease-in-out infinite' : 'none'
                  }}
                >
                  Live
                </span>
              </div>

              {/* Pipeline Stages with animated counters */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[
                  { label: 'New Leads', count: counters.leads, value: '$48K', color: '#3B82F6' },
                  { label: 'Qualified', count: counters.qualified, value: '$89K', color: '#8B5CF6' },
                  { label: 'Proposal', count: counters.proposal, value: '$156K', color: '#F59E0B' },
                  { label: 'Won', count: counters.won, value: '$124K', color: '#10B981' }
                ].map((stage, index) => (
                  <div 
                    key={index}
                    className="rounded-lg p-2 text-center relative overflow-hidden"
                    style={{ 
                      backgroundColor: stage.color + '10', 
                      border: `1px solid ${stage.color}30`,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                      transition: `all 0.4s ease-out ${300 + index * 100}ms`
                    }}
                  >
                    {/* Shimmer effect */}
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: `linear-gradient(90deg, transparent 0%, ${stage.color}15 50%, transparent 100%)`,
                        animation: isVisible ? `shimmer 2s ease-in-out ${index * 200}ms infinite` : 'none'
                      }}
                    />
                    <div className="text-[10px] font-medium relative z-10" style={{ color: stage.color }}>{stage.label}</div>
                    <div className="text-lg font-bold relative z-10" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{stage.count}</div>
                    <div className="text-[9px] relative z-10" style={{ color: isDark ? '#888888' : '#666666' }}>{stage.value}</div>
                  </div>
                ))}
              </div>

              {/* Lead Cards with staggered animation */}
              <div className="space-y-2">
                {[
                  { name: 'Sarah Johnson', company: 'TechCorp Inc', value: '$12,500', score: 92, hot: true },
                  { name: 'Mike Chen', company: 'StartupXYZ', value: '$8,200', score: 78, hot: false },
                  { name: 'Emma Wilson', company: 'Enterprise Co', value: '$24,000', score: 85, hot: true }
                ].map((lead, index) => (
                  <div 
                    key={index}
                    className="rounded-lg p-2 flex items-center justify-between"
                    style={{ 
                      backgroundColor: isDark ? '#222222' : '#FFFFFF', 
                      border: isDark ? '1px solid #333333' : '1px solid #E0E0E0',
                      opacity: showCards[index] ? 1 : 0,
                      transform: showCards[index] ? 'translateX(0) scale(1)' : 'translateX(-20px) scale(0.95)',
                      transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center"
                        style={{
                          animation: showCards[index] ? `bounceIn 0.5s ease-out ${index * 100}ms both` : 'none'
                        }}
                      >
                        <span className="text-[8px] font-medium text-white">{lead.name.charAt(0)}</span>
                      </div>
                      <div>
                        <div className="text-[10px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{lead.name}</div>
                        <div className="text-[8px]" style={{ color: isDark ? '#888888' : '#666666' }}>{lead.company}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>{lead.value}</span>
                      <div 
                        className="px-1.5 py-0.5 rounded text-[8px] font-medium"
                        style={{ 
                          backgroundColor: lead.hot ? '#EF444420' : '#F59E0B20',
                          color: lead.hot ? '#EF4444' : '#F59E0B',
                          animation: lead.hot && showCards[index] ? 'pulse 1.5s ease-in-out infinite' : 'none'
                        }}
                      >
                        {lead.score}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Stats with animated value */}
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div 
                  className="text-center p-2 rounded-lg" 
                  style={{ 
                    backgroundColor: isDark ? '#222222' : '#FFFFFF',
                    boxShadow: isVisible ? '0 0 20px rgba(16, 185, 129, 0.2)' : 'none',
                    transition: 'box-shadow 0.5s ease-out'
                  }}
                >
                  <span className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    ${pipelineValue}K
                  </span>
                  <p className="text-[10px]" style={{ color: isDark ? '#666666' : '#888888' }}>Pipeline Value</p>
                </div>
                <div className="text-center p-2 rounded-lg" style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF' }}>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>68%</span>
                    <TrendingUp className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />
                  </div>
                  <p className="text-[10px]" style={{ color: isDark ? '#666666' : '#888888' }}>Win Rate</p>
                </div>
                <div className="text-center p-2 rounded-lg" style={{ backgroundColor: isDark ? '#222222' : '#FFFFFF' }}>
                  <span className="text-lg font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>12 days</span>
                  <p className="text-[10px]" style={{ color: isDark ? '#666666' : '#888888' }}>Avg. Close</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default EditorialUGCAdsSection;
